import {
  ComboBoxWithExpression,
  Icon,
  Tooltip,
} from '@cotera/client/app/components';
import { Text } from '@cotera/client/app/components/ui';
import { TemplatedExpressionEditor } from '@cotera/client/app/components/eraql-editor';
import { useWatchableValue } from '@cotera/client/v0/actions/framework/watchable';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cotera/client/v0/components/ui/popover';
import { TC, Ty } from '@cotera/era';
import { Assert } from '@cotera/utilities';
import { Paperclip } from 'lucide-react';
import { useColumnModel } from '../context';
import { useContainerSize } from '@cotera/client/v0/components/data-grid/use-container-size';
import { useTextFieldTracking } from '@cotera/client/app/etc';

const ImagePopover: React.FC = () => {
  const model = useColumnModel();
  const { imageUrl } = useWatchableValue(model.formState);
  const datasetRel = useWatchableValue(model.datasetRel);

  const attributes =
    datasetRel === null || datasetRel.isErr()
      ? {}
      : (() => {
          Assert.assert(datasetRel.value.t === 'rel');
          return datasetRel.value.rel.attributes;
        })();

  const options =
    datasetRel === null || datasetRel.isErr()
      ? []
      : Object.entries(attributes)
          .filter(([k]) =>
            TC.implementsTy({
              req: Ty.ty('string'),
              subject: attributes[k]!,
            })
          )
          .map(([k]) => ({
            value: k,
            display: k,
          }));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon">
          <Paperclip />
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <ComboBoxWithExpression
          placeholder="Select an image"
          fieldValue={
            imageUrl ? { t: 'attr', value: imageUrl } : { t: 'unset' }
          }
          attributes={attributes}
          optional={true}
          onChange={(v) => {
            model.formState.set({
              ...model.formState.snapshot(),
              imageUrl: v.t === 'unset' ? null : v.value,
            });
          }}
          options={options}
        />
      </PopoverContent>
    </Popover>
  );
};

export const Prompt: React.FC = () => {
  const model = useColumnModel();
  const { prompt } = useWatchableValue(model.formState);
  const graph = useWatchableValue(model.inputs.graph);
  const parsedExpr = useWatchableValue(model.promptExpression);
  const trackChange = useTextFieldTracking('LLM Prompt Typed');

  const { containerRef, containerSize } = useContainerSize();

  // Calculate editor height based on container size
  // Reserve space for the header.
  const editorHeight = containerSize.height
    ? Math.max(containerSize.height - 54, 100)
    : 128;

  return (
    <div ref={containerRef} className="flex flex-col gap-2 w-full h-full px-1">
      <div className="flex flex-row items-center justify-between">
        <div className="text-sm text-foreground/50">Prompt</div>
        <div className="flex flex-row items-center gap-2">
          {parsedExpr.isErr() && (
            <Tooltip
              tooltipContent={JSON.stringify(parsedExpr.error)}
              side="top"
            >
              <div className="ml-4 mb-2 gap-1 group flex items-center">
                <Icon icon="exclamation-circle" className="text-destructive" />
                <Text.Caption className="text-destructive">
                  Invalid prompt
                </Text.Caption>
              </div>
            </Tooltip>
          )}
          <ImagePopover />
        </div>
      </div>
      <TemplatedExpressionEditor
        eraql={prompt}
        type="expr"
        inline
        height={editorHeight}
        parser={graph.parser.forRel(model.datasetName)}
        onChange={(v) => {
          model.formState.set({
            ...model.formState.snapshot(),
            prompt: v,
          });
          trackChange(v);
        }}
      />
    </div>
  );
};
